import { useEffect, useRef, useState } from "react";
import { useInView, useMotionValue, useTransform, animate } from "framer-motion";

/**
 * Number counter that counts up from 0 once scrolled into view.
 * Accepts numeric `value` (e.g. 28) or a string with suffix (e.g. "28+").
 */
export default function AnimatedCounter({
  value,
  duration = 1.6,
  className = "",
  testid,
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });

  // Pull number + suffix out of the input
  const str = String(value);
  const match = str.match(/^([0-9.,]+)(.*)$/);
  const num = match ? parseFloat(match[1].replace(/,/g, "")) : NaN;
  const suffix = match ? match[2] : "";
  const decimals = match && match[1].includes(".") ? match[1].split(".")[1].length : 0;
  const hasComma = match && match[1].includes(",");

  const motionVal = useMotionValue(0);
  const rounded = useTransform(motionVal, (v) => {
    if (Number.isNaN(num)) return str;
    const n = decimals > 0 ? v.toFixed(decimals) : Math.round(v).toString();
    const withCommas = hasComma ? Number(n).toLocaleString("en-US") : n;
    return withCommas + suffix;
  });

  const [text, setText] = useState(Number.isNaN(num) ? str : `0${suffix}`);

  useEffect(() => {
    const unsub = rounded.on("change", (v) => setText(v));
    return () => unsub();
  }, [rounded]);

  useEffect(() => {
    if (!inView || Number.isNaN(num)) return;
    const controls = animate(motionVal, num, {
      duration,
      ease: [0.22, 0.61, 0.36, 1],
    });
    return () => controls.stop();
  }, [inView, num, duration, motionVal]);

  return (
    <span ref={ref} data-testid={testid} className={className}>
      {text}
    </span>
  );
}
